import { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { FamilyBar, type FamilyDescriptor, type FamilyNavigation, type FamilyUnavailable } from '@beyond-js/ui/react';
import { Return } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import wordmark from '@brand/beyond-logo-wordmark.svg';
import { Notices } from './notices';
import { moved } from './screen';

/** The roles Projects offers an organization's invitations and settings to, as `links.manage` does */
const stewards = ['owner', 'admin'];

/**
 * The family bar every signed-in screen of this interface sits under (decisions D01 and D04): the
 * wordmark as the way home, "Accounts" as the product switcher, where the person is, the notification
 * entry and the account menu.
 *
 * What it shows is Projects' `beyond-family/1` descriptor, read through this service's relay for the
 * organization in view: the one a screen shows (`/organizations/:id`), else the one the session works
 * in, as in every product. The organizations collection names none: it lists every organization of the
 * person, and naming the session's beside them read as the list being scoped to it. The session keeps
 * that organization, which the bar never changes here. It is read again whenever the identity is, so a
 * rename or a removal reaches the bar. Only the
 * latest read is shown, and the previous answer stays until it arrives, so the bar never jumps back to
 * loading. When Projects cannot answer the bar shows the names and addresses this interface knows
 * itself (`fallback.links`), so "Your account" stays reachable.
 *
 * Accounts is where every product's profile menu leads (`links.manage`), so its own profile menu
 * leads to its own pages: the bar completes them with `product=accounts` and a return here, and both
 * are dropped when a link is followed in the application, so a member list reached from Accounts' own
 * bar was not sent here by another product and offers no way back to Accounts itself (`Return` also
 * ignores such a request when the link was opened another way). The organization menu changes the
 * organization in view here: each row is the organization's page in this interface, from the
 * descriptor or, while it loads or Projects cannot answer, from the identity document. "Sign out of
 * Beyond" opens `/leave`, as a product's sign-out does, with the bar's working state while it does.
 */
/** The organization's page in this interface, as an absolute address the bar treats as this origin's */
function page(organization: string): string {
	return new URL(`/organizations/${encodeURIComponent(organization)}`, window.location.origin).href;
}

/** This interface's pages for the profile menu while there is no descriptor, as Projects' `links.manage` names them */
function manage(membership?: { organization: string; role: string }): Record<string, string> {
	const links: Record<string, string> = { account: '/account', organizations: '/organizations', create: '/organizations?view=create' };
	if (!membership) return links;
	const organization = `/organizations/${encodeURIComponent(membership.organization)}`;
	links.members = `${organization}?view=members`;
	if (stewards.includes(membership.role)) Object.assign(links, { invitations: `${organization}?view=invitations`, settings: `${organization}?view=settings` });
	return links;
}

/**
 * The descriptor with each organization leading to its page here, and without Projects' `links.leave`:
 * that is this interface's own `/leave`, which the bar reaches inside the application (`after`). Without
 * an organization in view it names none: Projects answers for the one the session works in, which the
 * organizations collection is not about. An answer read for another organization stays until the one in
 * view arrives, so the bar never jumps back to loading, but it names the organization in view as this
 * interface knows it (`named`) and offers none of the other one's addresses (its GitHub, its members).
 */
function own(
	read: { viewed?: string; answer: FamilyDescriptor | FamilyUnavailable } | null,
	viewed?: string,
	named?: { id: string; name: string; role: string }
): FamilyDescriptor | FamilyUnavailable | null {
	if (!read || 'unavailable' in read.answer) return read?.answer ?? null;
	const descriptor = read.answer;
	const links = { ...descriptor.links };
	delete links.leave;
	// Accounts' organization navigation stays here; repository connections are managed in Projects.
	delete (links as { github?: string }).github;
	const organizations = descriptor.organizations?.map(entry => ({ ...entry, url: page(entry.id) }));
	if (viewed && read.viewed === viewed) return { ...descriptor, links, organizations };
	if (links.manage) links.manage = Object.fromEntries(Object.entries(links.manage).filter(([key]) => !['members', 'invitations', 'settings'].includes(key)));
	return { ...descriptor, links, organizations, organization: viewed && named ? named : undefined };
}

export function Bar() {
	const { identity, family, language } = usePresence();
	// The family's own copy in the interface language (0.7.2), areas and roles included
	const navigate = useNavigate();
	const shown = useMatch('/organizations/:organization')?.params.organization;
	const collection = useMatch('/organizations') !== null;
	const viewed = shown ?? (collection ? undefined : (identity?.session.organization ?? undefined));

	// The latest answer, with the organization it was read for
	const [descriptor, setDescriptor] = useState<{ viewed?: string; answer: FamilyDescriptor | FamilyUnavailable } | null>(null);
	const [home, setHome] = useState<string>();
	const latest = useRef(0);

	useEffect(() => {
		void family.home().then(setHome);
	}, [family]);

	useEffect(() => {
		const turn = ++latest.current;
		void family.read(viewed).then(answer => {
			if (turn === latest.current) setDescriptor({ viewed, answer });
		});
	}, [family, viewed, identity]);

	const membership = identity?.organizations.find(entry => entry.organization === viewed);
	// The person as their own identity document names them: the profile menu heads with the name and
	// their own verified address (D78), shown to them and kept nowhere
	const name = identity?.account.name;
	const email = identity?.account.email;
	// The names and addresses this interface knows itself, for while the descriptor loads or Projects
	// cannot answer: the person's organizations, its own pages for the profile menu, and Projects when known
	const fallback = useMemo(
		() => ({
			person: name ? (email ? { name, email } : name) : null,
			organization: membership ? (membership.name ?? membership.organization) : null,
			organizations: (identity?.organizations ?? []).map(entry => ({
				id: entry.organization,
				name: entry.name ?? entry.organization,
				role: entry.role,
				url: page(entry.organization),
				current: entry.organization === viewed
			})),
			links: {
				account: '/account',
				manage: manage(membership),
				...(home && home !== '/' ? { home } : {})
			}
		}),
		[name, email, identity?.organizations, membership, viewed, home]
	);
	// Each organization of the descriptor is the organization's page here, as in the fallback
	const named = membership ? { id: membership.organization, name: membership.name ?? membership.organization, role: membership.role } : undefined;
	const relayed = useMemo(() => own(descriptor, viewed, named), [descriptor, viewed, named?.id, named?.name, named?.role]);

	// "Sign out of Beyond" (Q09), in the package's form: the session held here is the Beyond session, so
	// there is no product session to end first, and with no `links.leave` the bar calls `after`, which
	// opens the page every product's sign-out lands on inside the application. `/leave` ends the Beyond
	// session and every product session derived from it within its deadline and says what happened
	const account = useMemo(() => ({ signout: { after: () => navigate('/leave') } }), [navigate]);

	function follow(item: FamilyNavigation) {
		const url = new URL(item.url);
		url.searchParams.delete(Return.PRODUCT);
		url.searchParams.delete(Return.ADDRESS);
		navigate(`${url.pathname}${url.search}${url.hash}`, { state: moved });
	}

	return (
		<FamilyBar
			product="accounts"
			// Accounts has no projects: the bar draws the organization only, never "Choose a project"
			projects={false}
			brand={{ src: wordmark, href: '/' }}
			descriptor={relayed}
			fallback={fallback}
			labels={FamilyBar.labels[language]}
			notifications={<Notices />}
			account={account}
			onNavigate={follow}
		/>
	);
}
